import TrashCan from "@assets/images/trash.svg";
import { FormControlLabel, Grid, Radio, RadioGroup } from "@material-ui/core";
import InputSuggestion from "@saleor/changeOrderExport/components/InputSuggestion";
import MultipleSelect from "@saleor/changeOrderExport/components/OrderStatus";
import MyTypography from "@saleor/components/MyTypography";
import SingleTextField from "@saleor/components/SingleTextField";
import TextInputDisable from "@saleor/components/TextInputDisable";
import {
  ScgpUserQuery,
  useFilterSoldToScgCheckoutLazyQuery,
  useGetRoleListByGroupQuery
} from "@saleor/graphql";
import UserAD from "@saleor/userManagement/components/UserAD";
import { CustomerType, UserDetailMode } from "@saleor/userManagement/constants";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import FormCustomerSoldToList from "./FormCustomerSoldToList";
import FormSwitchActive from "./FormSwtichActive";
import useStyles from "./styles";

interface FormCustomerProps {
  formik?: any;
  groupId?: string;
  user?: ScgpUserQuery;
  handleOpenDelete: () => void;
  handleOnKeyPress: (e: any) => void;
  handleOnPaste: (e: any) => void;
  mode?: UserDetailMode;
}

const FormCustomer: React.FC<FormCustomerProps> = ({
  formik,
  groupId,
  user,
  handleOpenDelete,
  handleOnKeyPress,
  handleOnPaste,
  mode
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [isClean, setIsClean] = useState(false);
  const [soldToPools, setSoldToPools] = useState([]);
  const [groupInitialized, setGroupInitialized] = useState(false);

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useFilterSoldToScgCheckoutLazyQuery();

  const {
    data: dataRoleListByGroupId,
    loading: loadingRoleListByGroupId,
    refetch: refetchRoleListByGroupId
  } = useGetRoleListByGroupQuery({
    variables: {
      id: groupId
    }
  });

  useEffect(() => {
    if (!groupInitialized && groupId) {
      setGroupInitialized(true);
    } else if (groupInitialized) {
      formik.setFieldValue("userRoles", []);
    }
    refetchRoleListByGroupId();
  }, [groupId]);

  const soldToOptions = (
    mapEdgesToItems(fetchSoldToDataSuggestions?.filterSoldToScgCheckout) || []
  ).map(data => ({
    title: `${data.code} - ${data.name}`,
    value: data.id
  }));

  useEffect(() => {
    if (user?.scgpUser?.soldTos) {
      const appendedPool = [];
      for (const i of user.scgpUser.soldTos) {
        if (!soldToPools.map(x => x.value).includes(i.id)) {
          appendedPool.push({ title: `${i.code} - ${i.name}`, value: i.id });
        }
      }
      setSoldToPools([...soldToPools, ...appendedPool]);
    }
  }, [user?.scgpUser?.soldTos]);

  const soldToValues = useMemo(
    () => soldToPools.filter(x => formik.values.soldTo.includes(x.value)),
    [formik.values.soldTo, soldToPools]
  );

  const userRoleOptions =
    !loadingRoleListByGroupId &&
    dataRoleListByGroupId?.parentGroup?.groups?.map(data => ({
      label: data.name,
      value: data.id
    }));

  const cleanSoldToInput = useCallback(() => {
    setIsClean(true);
    setTimeout(() => setIsClean(false));
  }, []);

  const handleDeleteChip = id => {
    formik.setFieldValue(
      "soldTo",
      formik.values.soldTo.filter(x => x !== id.toString())
    );
    setSoldToPools(soldToPools.filter(x => x.value !== id.toString()));
  };

  const getErrorText = field =>
    mode !== UserDetailMode.VIEW ? (
      <div className={classes.textError}>{formik.errors[field]}</div>
    ) : (
      ""
    );

  return (
    <>
      {mode === UserDetailMode.VIEW && (
        <>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Display Name" })}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="displayName-text">
              {user?.scgpUser?.extendData?.displayName || "-"}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "User Roles" })}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="userRoles-text">
              {user?.scgpUser?.permissionGroups?.map(x => x?.name)?.join(", ")}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Customer Type" })}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="customerType-text">
              {user?.scgpUser?.extendData?.customerType}
            </MyTypography>
          </Grid>
          <Grid item md={6} />
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "E-mail" })}
              <span className={classes.lighter}> (ใช้ในการ Login)</span>
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="email-text">
              {user?.scgpUser?.email}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Company E-mail" })}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="companyEmail-text">
              {user?.scgpUser?.extendData?.companyEmail}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "SAP ID" })}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="sapId-text">
              {user?.scgpUser?.extendData?.sapId || "-"}
            </MyTypography>
          </Grid>
          <Grid item md={6} />
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sold To" })}
            </MyTypography>
          </Grid>
        </>
      )}
      {mode !== UserDetailMode.VIEW && (
        <>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "ชื่อ" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            {formik.values.customerType === CustomerType.INTERNAL ? (
              <TextInputDisable
                value={formik.values.firstName || ""}
                testId="firstName-readonlyInput"
              />
            ) : (
              <SingleTextField
                name={"firstName"}
                id="firstName"
                placeholder="ระบุชื่อ"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={!!formik.errors.firstName}
              />
            )}
            {getErrorText("firstName")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "นามสกุล" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            {formik.values.customerType === CustomerType.INTERNAL ? (
              <TextInputDisable
                value={formik.values.lastName || ""}
                testId="lastName-readonlyInput"
              />
            ) : (
              <SingleTextField
                name={"lastName"}
                id="lastName"
                placeholder="ระบุนามสกุล"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={!!formik.errors.lastName}
              />
            )}
            {getErrorText("lastName")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Display Name" })}
            </MyTypography>
            <SingleTextField
              name={"displayName"}
              id="displayName"
              placeholder="ระบุ Display Name"
              value={formik.values.displayName}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "User Roles" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            {userRoleOptions && userRoleOptions.length > 0 && (
              <MultipleSelect
                showChipDeleteButton={false}
                showCircularIcon={false}
                choices={userRoleOptions}
                name="userRoles"
                placeholder="เลือก User Roles"
                numberItemDisplay={2}
                value={formik.values.userRoles}
                onChange={formik.handleChange}
                error={!!formik.errors.userRoles}
              />
            )}
            {getErrorText("userRoles")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Customer Type" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="customerType"
              id="customerType"
              value={formik.values.customerType}
              onChange={formik.handleChange}
            >
              {[
                { label: "Internal", value: CustomerType.INTERNAL },
                { label: "External", value: CustomerType.EXTERNAL }
              ].map(option => (
                <FormControlLabel
                  key={`radio${option.value}`}
                  id={`radio${option.value}`}
                  data-testid="customerType-option"
                  value={option.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>
                      {option.label}
                    </MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          </Grid>
          <Grid item md={6}>
            {formik.values.customerType === CustomerType.INTERNAL && (
              <UserAD formik={formik} />
            )}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "E-mail" })}
              <span className={classes.lighter}> (ใช้ในการ Login)</span>
              <span className={classes.required}>*</span>
            </MyTypography>
            {formik.values.customerType === CustomerType.INTERNAL ? (
              <TextInputDisable
                value={formik.values.email || ""}
                testId="email-readonlyInput"
              />
            ) : (
              <SingleTextField
                name={"email"}
                id="email"
                placeholder="ระบุ E-mail"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={!!formik.errors.email}
              />
            )}
            {getErrorText("email")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Company E-mail" })}
            </MyTypography>
            <SingleTextField
              name={"companyEmail"}
              id="companyEmail"
              placeholder="ระบุ Company E-mail"
              value={formik.values.companyEmail}
              onChange={formik.handleChange}
              error={!!formik.errors.companyEmail}
            />
            {getErrorText("companyEmail")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "SAP ID" })}
            </MyTypography>
            <SingleTextField
              name={"sapId"}
              id="sapId"
              placeholder="ระบุ SAP ID"
              value={formik.values.sapId}
              onChange={formik.handleChange}
              onKeyPress={handleOnKeyPress}
              onPaste={handleOnPaste}
              maxLengthProps={12}
              textUpperCase
            />
          </Grid>
          <Grid item md={6} />
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sold To" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            <InputSuggestion
              dataOptions={soldToOptions}
              fetchDataSuggestions={fetchSoldToDataSuggestions}
              loadingDataSuggestion={loadingSoldToDataSuggestion}
              getDataSuggestions={getSoldToDataSuggestions}
              contained
              name={"soldTo"}
              id="soldTo"
              placeholder="ค้นหา Sold To"
              isClean={isClean}
              error={!!formik.errors.soldTo}
              isCustomer={true}
              handleSelectedSearch={e => {
                const selected = soldToOptions.find(x => x.title === e);
                const id = selected?.value;
                setSoldToPools([...soldToPools, selected]);
                if (e && !formik.values.soldTo.includes(id)) {
                  formik.setFieldValue("soldTo", [...formik.values.soldTo, id]);
                }
                cleanSoldToInput();
              }}
            />
            {getErrorText("soldTo")}
          </Grid>
          <Grid item md={6} />
        </>
      )}
      <FormCustomerSoldToList
        mode={mode}
        values={soldToValues}
        onDelete={handleDeleteChip}
      />
      <FormSwitchActive formik={formik} user={user} mode={mode} />
      {mode === UserDetailMode.EDIT ? (
        <Grid item md={6}>
          <SVG
            src={TrashCan}
            cursor="pointer"
            onClick={handleOpenDelete}
            data-testid="deleteUserButton"
          />
        </Grid>
      ) : null}
    </>
  );
};

export default FormCustomer;
